/**
 * Typed access to client-side env vars baked into the Vite build.
 *
 * Vite exposes any variable prefixed with `VITE_` via `import.meta.env`,
 * inlined at build time. ANYTHING surfaced here is visible to anyone with
 * the production bundle — never put a real secret behind a `VITE_` prefix.
 *
 * Server-side secrets (JWT_SECRET, etc.) live in the backend's
 * `app/lib/config.py` and never reach the frontend.
 *
 * Adding a new client env var? Update this file, `.env.example`, and the
 * test in `env.test.ts` in the same commit.
 */

export type ClientEnv = {
  apiBaseUrl: string
  sentryDsn: string | undefined
  posthogKey: string | undefined
  posthogHost: string | undefined
  /** AMTC call-centre number, shown wherever the answer is "a person will
   * arrange this" — the appointment fallback (M14-T08), the ambulance screen,
   * and the unstaffed-discipline reading (M22-T12).
   *
   * The default IS the real number, owner-supplied 2026-08-31. It is not a
   * secret — it is printed on the screen — so it lives here rather than in
   * per-environment config, and one source means the three call buttons cannot
   * disagree. `VITE_AMTC_PHONE` still overrides it if the line ever changes
   * before a deploy can. Display form; render `tel:` hrefs through
   * `dialablePhone`. */
  amtcPhone: string
}

function nonEmpty(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim()
  return trimmed.length === 0 ? undefined : trimmed
}

export const env: ClientEnv = {
  apiBaseUrl: nonEmpty(import.meta.env.VITE_API_BASE_URL) ?? 'http://localhost:8000',
  sentryDsn: nonEmpty(import.meta.env.VITE_SENTRY_DSN),
  posthogKey: nonEmpty(import.meta.env.VITE_POSTHOG_KEY),
  posthogHost: nonEmpty(import.meta.env.VITE_POSTHOG_HOST),
  // Corrected 2026-10-25 (owner): the number ends in 1351, not 1354.
  amtcPhone: nonEmpty(import.meta.env.VITE_AMTC_PHONE) ?? '+233 55 064 1351',
}
